import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ShareIcon from "@mui/icons-material/Share";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import VideocamIcon from "@mui/icons-material/Videocam";
import { Box, Typography, Button, IconButton } from "@mui/material";
import React from "react";

import { MediaItem } from "./PreviewPane";

// Globe icon used in Facebook and LinkedIn previews
const Globe: React.FC = () => (
  <Box component="svg" viewBox="0 0 16 16" fill="currentColor" sx={{ width: 16, height: 16 }}>
    <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm5.91 7h-2.03c-.08-1.37-.39-2.67-.88-3.81 1.68.83 2.91 2.31 2.91 3.81zM8 2c.55 0 1.51 1.21 1.91 3H6.09c.4-1.79 1.36-3 1.91-3zm-5.91 5h2.03c.08 1.37.39 2.67.88 3.81-1.68-.83-2.91-2.31-2.91-3.81zM8 14c-.55 0-1.51-1.21-1.91-3h3.82c-.4 1.79-1.36 3-1.91 3zm2.15-4H5.85c-.09-1.32-.09-2.68 0-4h4.3c.09 1.32.09 2.68 0 4z" />
  </Box>
);

interface FacebookPreviewProps {
  content: string;
  media: MediaItem[];
}

export const FacebookPreview: React.FC<FacebookPreviewProps> = ({ content, media }) => {
  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "grey.300",
              flexShrink: 0,
            }}
          />
          <Box sx={{ ml: 2, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Il tuo account
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: "grey.500" }}>
                Ora ·
              </Typography>
              <Box sx={{ width: 16, height: 16, flexShrink: 0 }}>
                <Globe />
              </Box>
            </Box>
          </Box>
        </Box>
        <IconButton sx={{ p: 1, "&:hover": { bgcolor: "grey.100" }, flexShrink: 0 }}>
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            overflowWrap: "break-word",
            wordWrap: "break-word",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          {content || (
            <span style={{ color: "grey" }}>
              Il tuo post apparirà qui. Scrivi qualcosa di interessante!
            </span>
          )}
        </Typography>
        {media.length > 0 && (
          <Box
            sx={{
              mb: 2,
              display: media.length === 1 ? "block" : "grid",
              gridTemplateColumns: media.length === 1 ? "1fr" : "1fr 1fr",
              gap: 1,
            }}
          >
            {media.map((item) => (
              <Box key={item.id || item.url} sx={{ position: "relative" }}>
                {item.type === "video" ? (
                  <>
                    <Box
                      component="video"
                      src={item.url}
                      controls
                      width="100%"
                      sx={{
                        objectFit: "cover",
                        maxHeight: 400,
                        borderRadius: 1,
                      }}
                    />
                    {/* Video indicator overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(0,0,0,0.6)",
                        color: "white",
                        borderRadius: "50%",
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                      }}
                    >
                      <VideocamIcon fontSize="small" />
                    </Box>
                  </>
                ) : (
                  <Box
                    component="img"
                    src={item.url}
                    alt=""
                    sx={{ width: "100%", objectFit: "cover", maxHeight: 400, borderRadius: 1 }}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1,
            borderTop: 1,
            borderColor: "divider",
            color: "grey.500",
            flexWrap: "wrap",
          }}
        >
          <Button
            sx={{
              textTransform: "none",
              px: { xs: 1, sm: 2 },
              py: 1,
              borderRadius: 2,
              "&:hover": { bgcolor: "grey.100" },
              minWidth: "auto",
              flexGrow: { xs: 1, sm: 0 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
            startIcon={<ThumbUpAltOutlinedIcon fontSize="small" />}
          >
            Like
          </Button>
          <Button
            sx={{
              textTransform: "none",
              px: { xs: 1, sm: 2 },
              py: 1,
              borderRadius: 2,
              "&:hover": { bgcolor: "grey.100" },
              minWidth: "auto",
              flexGrow: { xs: 1, sm: 0 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
            startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
          >
            Commenta
          </Button>
          <Button
            sx={{
              textTransform: "none",
              px: { xs: 1, sm: 2 },
              py: 1,
              borderRadius: 2,
              "&:hover": { bgcolor: "grey.100" },
              minWidth: "auto",
              flexGrow: { xs: 1, sm: 0 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
            startIcon={<ShareIcon fontSize="small" />}
          >
            Condividi
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
